import React from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Visibility as VisibilityIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { QuestionConditionStats } from '@/features/questionnaires/hooks/questionnaire/useQuestionConditionStats';
import { QuestionConditionButtonBadges } from '../conditions/QuestionConditionButtonBadges';
import { QuestionnaireFormData } from '@/features/questionnaires';
import { useFormContext } from 'react-hook-form';

type QuestionHeaderProps = {
  index: number;
  onConditionsClick: () => void;
  onDelete: () => void;
  dragHandleProps: React.HTMLAttributes<HTMLElement>;
  conditionsCount: number;
  questionStats: QuestionConditionStats | undefined;
};

export const QuestionHeader: React.FC<QuestionHeaderProps> = ({
  index,
  onConditionsClick,
  onDelete,
  dragHandleProps,
  conditionsCount,
  questionStats,
}) => {
  const { t } = useTranslation();
  const {
    getFieldState,
    formState: { isSubmitting },
  } = useFormContext<QuestionnaireFormData>();

  const conditionsError = getFieldState(
    `questions.${index}.displayConditions`,
  ).error;
  const errorsCount = Object.keys(conditionsError ?? {}).length;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box {...dragHandleProps}>
          <IconButton
            size="small"
            sx={{
              cursor: 'grab',
              '&:active': {
                cursor: 'grabbing',
              },
            }}
            aria-label={t('buttons.dragToReorder')}
          >
            <DragIndicatorIcon />
          </IconButton>
        </Box>
        <Typography variant="subtitle1">
          {t('pages.studies.questions.title', {
            index: index + 1,
          })}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button
          onClick={onConditionsClick}
          disabled={isSubmitting}
          size="small"
          startIcon={<VisibilityIcon />}
          color="inherit"
          variant="outlined"
          sx={{
            textTransform: 'none',
            borderColor: 'divider',
            '&:hover': {
              borderColor: 'action.hover',
              backgroundColor: 'action.hover',
            },
          }}
        >
          {t('buttons.conditions')}
          <QuestionConditionButtonBadges
            conditionsCount={conditionsCount}
            dependentCount={questionStats?.dependentQuestionsCount ?? 0}
            invalidCount={errorsCount}
          />
        </Button>
        <IconButton
          onClick={onDelete}
          disabled={isSubmitting}
          color="error"
          size="small"
          aria-label={t('buttons.delete')}
        >
          <DeleteIcon />
        </IconButton>
      </Box>
    </Box>
  );
};
